import './Login.css';

// Libraries
import React, { useState } from 'react';
import User from 'utils/user.util';

// Assets
import { Card, Col, Form, Row, Alert } from 'react-bootstrap';
import { useEffect } from 'react';
import {
    client,
    useLazyQuery,
    GET_USER_BY_USERNAME,
    UPDATE_USER_LAST_LOGIN,
    useMutation,
} from 'utils/graphql';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import { Link } from 'react-router-dom';
import { formatUTCDateTime } from 'utils/date.util';
import { useTranslation } from 'react-i18next';

async function loginUser(credentials) {
    return fetch(`${process.env.REACT_APP_PROXY_URL || ''}/login`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
    })
        .then((data) => data.json())
        .catch((error) => {
            console.log({ error });
        });
}

export default function Login({ setToken }) {
    const { t } = useTranslation();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [validated, setValidated] = useState(false);
    const [alert, setAlert] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const [getUserByUsername] = useLazyQuery(GET_USER_BY_USERNAME, { client });

    const [updateUserLastLogin] = useMutation(UPDATE_USER_LAST_LOGIN, { client });

    const login = async (event) => {
        event.preventDefault();
        setIsLoading(true);
        if (!validate()) {
            setIsLoading(false);
            return;
        }

        const tokenResponse = await loginUser({
            username: username.toLowerCase(),
            password,
        });

        if (!tokenResponse) {
            // auth proxy is not responding
            setAlert({
                type: 'danger',
                message: 'login_failed',
            });
        } else if (tokenResponse?.status === 503) {
            // CDP is not responding
            setAlert({
                type: 'danger',
                message: 'service_unavailable',
            });
        } else if (tokenResponse?.status === 401) {
            // user is inactive/deleted
            setAlert({
                type: 'danger',
                message: 'user_inactive_error',
            });
        } else if (tokenResponse && tokenResponse.token) {
            const { data } = await getUserByUsername({
                variables: { username: username.toLowerCase() },
                context: {
                    headers: {
                        Authorization: `Bearer ${tokenResponse.token}`,
                    },
                },
            });

            if (data && data.table) {
                const user = data.table.records[0].propertyuser__c;
                User.init(user);
                updateUserLastLogin({
                    variables: { id: user.id, last_login: formatUTCDateTime() },
                    context: {
                        headers: {
                            Authorization: `Bearer ${tokenResponse.token}`,
                        },
                    },
                });
                setToken(tokenResponse);
            } else {
                setAlert({
                    type: 'danger',
                    message: 'login_failed',
                });
            }
        } else {
            setAlert({
                type: 'danger',
                message: 'credentials_dont_match',
            });
        }
        setIsLoading(false);
    };

    const validate = () => {
        setAlert(null);
        setUsernameError('');
        setPasswordError('');
        let errors = false;
        if (!username) {
            setUsernameError('username_empty_error');
            errors = true;
        }
        if (!password) {
            setPasswordError('password_empty_error');
            errors = true;
        }

        if (errors) {
            setValidated(true);
            return false;
        }
        setUsername(username.trim());
        setPassword(password.trim());
        return true;
    };

    useEffect(() => {
        if (validated) setValidated(false);
    }, [username, password]);

    return (
        <div className="login container">
            {alert && (
                <Row className="mt-4 d-flex justify-content-center">
                    <Col md={6}>
                        <Alert variant={alert.type} className="mb-0">
                            {t(alert.message)}
                        </Alert>
                    </Col>
                </Row>
            )}
            <Row className="mt-4 d-flex justify-content-center">
                <Col md={6}>
                    <Card>
                        <Card.Header>{t('login')}</Card.Header>
                        <Card.Body>
                            <Form onSubmit={login}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('username')}</Form.Label>
                                    <Form.Control
                                        isInvalid={validated && usernameError}
                                        type="text"
                                        value={username}
                                        autoComplete="username"
                                        onChange={(event) => setUsername(event.target.value)}
                                    />
                                    {usernameError && (
                                        <Form.Control.Feedback type="invalid">
                                            {t(usernameError)}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('password')}</Form.Label>
                                    <Form.Control
                                        isInvalid={validated && passwordError}
                                        type="password"
                                        value={password}
                                        autoComplete="current-password"
                                        onChange={(event) => setPassword(event.target.value)}
                                    />
                                    {passwordError && (
                                        <Form.Control.Feedback type="invalid">
                                            {t(passwordError)}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                                <div className="d-flex justify-content-between align-items-center">
                                    <Link to="/forgotpassword">{t('forgot_your_password')}</Link>
                                    <LoadingButton type="submit" isLoading={isLoading}>
                                        {t('login')}
                                    </LoadingButton>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}
